import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Users, AlertCircle, LogOut, Download } from 'lucide-react';
import { database, auth, signInWithMicrosoft } from './firebase';
import { ref, onValue, set, remove, push } from 'firebase/database';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import ToastContainer from './ToastContainer';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const OnboardingCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [onboardings, setOnboardings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [formData, setFormData] = useState({ customer: '', status: 'confirmed', notes: '' });
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [inlineNotes, setInlineNotes] = useState({});
  const [operationLoading, setOperationLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [toasts, setToasts] = useState([]);
  
  // Rate limiting state
  const operationTimestamps = useRef([]);
  const MAX_OPERATIONS_PER_MINUTE = 10;

  // Toast notification helper
  const showToast = (message, type = 'info', duration = 3000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Handle Firebase authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log('Auth state changed:', currentUser);
      setUser(currentUser);
      setAuthLoading(false);
      if (!currentUser) {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle Microsoft login
  const handleLogin = async () => {
    try {
      setAuthLoading(true);
      await signInWithMicrosoft();
    } catch (error) {
      console.error('Login error:', error);
      showToast(`Login failed: ${error.message}`, 'error');
      setAuthLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Rate limiting check
  const checkRateLimit = () => {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    // Remove operations older than 1 minute
    operationTimestamps.current = operationTimestamps.current.filter(
      timestamp => timestamp > oneMinuteAgo
    );
    
    // Check if limit exceeded
    if (operationTimestamps.current.length >= MAX_OPERATIONS_PER_MINUTE) {
      return false;
    }
    
    // Add current operation
    operationTimestamps.current.push(now);
    return true;
  };

  // Create audit log entry
  const createAuditLog = async (action, recordId, details = '') => {
    if (!user) return;
    
    try {
      const auditLogRef = push(ref(database, 'auditLogs'));
      await set(auditLogRef, {
        action,
        userId: user.uid,
        userEmail: user.email || user.displayName,
        timestamp: Date.now(),
        recordId,
        details
      });
    } catch (error) {
      console.error('Error creating audit log:', error);
      // Don't fail the operation if audit logging fails
    }
  };

  // Load onboardings from Firebase
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const onboardingsRef = ref(database, 'onboardings');
    let isFirstLoad = true;
    
    const unsubscribe = onValue(onboardingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Convert Firebase object to array
        const onboardingsArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setOnboardings(onboardingsArray);
      } else if (isFirstLoad) {
        // Only initialize with default data on first load if database is empty
        const now = Date.now();
        const defaultData = {
          '1': { 
            customer: 'Acme Corp', 
            startDate: '2025-10-15', 
            status: 'confirmed',
            createdBy: 'system',
            createdAt: now
          },
          '2': { 
            customer: 'TechStart Inc', 
            startDate: '2025-10-22', 
            status: 'confirmed',
            createdBy: 'system',
            createdAt: now
          }
        };
        set(onboardingsRef, defaultData);
      } else {
        // Database is empty but it's not first load - user deleted everything
        setOnboardings([]);
      }
      setLoading(false);
      isFirstLoad = false;
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [user]);

  const statuses = [
    { value: 'confirmed', label: 'Confirmed', color: 'bg-red-500' },
    { value: 'pending', label: 'Pending', color: 'bg-yellow-500' },
    { value: 'available', label: 'Available Slot', color: 'bg-green-500' }
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getMonthOnboardings = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    
    return onboardings.filter(ob => {
      const startDate = new Date(ob.startDate);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 29); // 30 days total (start day + 29 more)
      
      // Check if the onboarding period overlaps with the current month
      return (startDate <= monthEnd && endDate >= monthStart);
    });
  };

  // Get all onboardings that overlap with a specific day
  const getOnboardingsForDay = (day, month, year) => {
    const checkDate = new Date(year, month, day);
    
    return onboardings.filter(ob => {
      const startDate = new Date(ob.startDate);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 29); // 30 days total
      
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  const getCapacityInfo = (date) => {
    const monthOnboardings = getMonthOnboardings(date);
    const confirmed = monthOnboardings.filter(ob => ob.status === 'confirmed').length;
    const pending = monthOnboardings.filter(ob => ob.status === 'pending').length;
    const available = 3 - confirmed;
    
    return { confirmed, pending, available, total: 3 };
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDayClick = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setShowModal(true);
  };

  const handleScheduleOnboard = () => {
    // Set default date to today
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setShowModal(true);
  };

  const handleAddOnboarding = async () => {
    console.log('Add Onboarding clicked', { formData, selectedDate });
    
    if (!formData.customer.trim()) {
      showToast('Please enter a customer name', 'warning');
      return;
    }

    // Check rate limit
    if (!checkRateLimit()) {
      showToast('Too many operations. Please wait a minute before trying again.', 'warning');
      return;
    }

    const capacity = getCapacityInfo(currentDate);
    
    if (formData.status === 'confirmed' && capacity.available <= 0) {
      showToast('Cannot add more confirmed onboardings. Monthly capacity of 3 reached!', 'warning');
      return;
    }

    setOperationLoading(true);
    try {
      // Generate a unique ID
      const newId = Date.now().toString();
      const now = Date.now();
      const newOnboarding = {
        customer: formData.customer,
        startDate: selectedDate,
        status: formData.status,
        notes: formData.notes || '',
        createdBy: user.email || user.displayName || user.uid,
        createdAt: now
      };
      
      console.log('Saving to Firebase:', newOnboarding);
      
      // Save to Firebase
      const onboardingRef = ref(database, `onboardings/${newId}`);
      await set(onboardingRef, newOnboarding);
      
      // Create audit log
      await createAuditLog('create', newId, `Created onboarding for ${formData.customer}`);
      
      console.log('Successfully saved to Firebase');
      
      showToast('Onboarding added successfully!', 'success');
      setShowModal(false);
      setFormData({ customer: '', status: 'confirmed', notes: '' });
    } catch (error) {
      console.error('Error saving onboarding:', error);
      showToast(`Error saving onboarding: ${error.message}`, 'error');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDelete = async (id) => {
    // Check rate limit
    if (!checkRateLimit()) {
      showToast('Too many operations. Please wait a minute before trying again.', 'warning');
      return;
    }

    setOperationLoading(true);
    try {
      // Get the onboarding details before deleting for audit log
      const onboarding = onboardings.find(ob => ob.id === id);
      
      // Delete from Firebase
      const onboardingRef = ref(database, `onboardings/${id}`);
      await remove(onboardingRef);
      
      // Create audit log
      if (onboarding) {
        await createAuditLog('delete', id, `Deleted onboarding for ${onboarding.customer}`);
      }
      
      showToast('Onboarding deleted successfully', 'success');
      // Close confirmation dialog
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting onboarding:', error);
      showToast(`Error deleting onboarding: ${error.message}`, 'error');
    } finally {
      setOperationLoading(false);
    }
  };

  const confirmDelete = (id, customer) => {
    setDeleteTarget({ id, customer });
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleEdit = (onboarding) => {
    setEditingId(onboarding.id);
    setFormData({
      customer: onboarding.customer,
      status: onboarding.status,
      notes: onboarding.notes || ''
    });
    setSelectedDate(onboarding.startDate);
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    if (!formData.customer.trim()) {
      showToast('Please enter a customer name', 'warning');
      return;
    }

    // Check rate limit
    if (!checkRateLimit()) {
      showToast('Too many operations. Please wait a minute before trying again.', 'warning');
      return;
    }

    setOperationLoading(true);
    try {
      const now = Date.now();
      const updatedOnboarding = {
        customer: formData.customer,
        startDate: selectedDate,
        status: formData.status,
        notes: formData.notes || '',
        modifiedBy: user.email || user.displayName || user.uid,
        modifiedAt: now
      };

      // Keep existing createdBy and createdAt
      const existing = onboardings.find(ob => ob.id === editingId);
      if (existing) {
        updatedOnboarding.createdBy = existing.createdBy || user.email || user.displayName || user.uid;
        updatedOnboarding.createdAt = existing.createdAt || now;
      } else {
        // Fallback if existing record not found
        updatedOnboarding.createdBy = user.email || user.displayName || user.uid;
        updatedOnboarding.createdAt = now;
      }

      // Update in Firebase
      const onboardingRef = ref(database, `onboardings/${editingId}`);
      await set(onboardingRef, updatedOnboarding);

      // Create audit log
      await createAuditLog('update', editingId, `Updated onboarding for ${formData.customer}`);

      showToast('Onboarding updated successfully!', 'success');
      setShowModal(false);
      setEditingId(null);
      setFormData({ customer: '', status: 'confirmed', notes: '' });
    } catch (error) {
      console.error('Error updating onboarding:', error);
      showToast(`Error updating onboarding: ${error.message}`, 'error');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ customer: '', status: 'confirmed', notes: '' });
  };

  const handleInlineNoteSave = async (id) => {
    // Check rate limit
    if (!checkRateLimit()) {
      showToast('Too many operations. Please wait a minute before trying again.', 'warning');
      return;
    }

    setOperationLoading(true);
    try {
      const onboarding = onboardings.find(ob => ob.id === id);
      if (!onboarding) {
        throw new Error('Onboarding not found');
      }

      const noteText = inlineNotes[id] !== undefined ? inlineNotes[id] : (onboarding.notes || '');
      const now = Date.now();
      
      const updatedOnboarding = {
        customer: onboarding.customer,
        startDate: onboarding.startDate,
        status: onboarding.status,
        notes: noteText,
        modifiedBy: user.email || user.displayName || user.uid,
        modifiedAt: now,
        createdBy: onboarding.createdBy || user.email || user.displayName || user.uid,
        createdAt: onboarding.createdAt || now
      };

      const onboardingRef = ref(database, `onboardings/${id}`);
      await set(onboardingRef, updatedOnboarding);

      await createAuditLog('update', id, `Updated notes for ${onboarding.customer}`);
      showToast('Note saved successfully!', 'success');
      
      // Clear the inline notes state for this entry after successful save
      const newInlineNotes = { ...inlineNotes };
      delete newInlineNotes[id];
      setInlineNotes(newInlineNotes);
    } catch (error) {
      console.error('Error saving note:', error);
      showToast(`Error saving note: ${error.message}`, 'error');
    } finally {
      setOperationLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
    
    // Add title
    doc.setFontSize(18);
    doc.text('Aptarro Customer Onboarding Calendar', 14, 15);
    
    // Add export date
    doc.setFontSize(10);
    doc.text(`Exported: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, 14, 22);
    
    // Prepare table data
    const tableData = onboardings.map(ob => {
      const startDate = new Date(ob.startDate);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 29);
      
      return [
        ob.customer,
        startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        ob.status.charAt(0).toUpperCase() + ob.status.slice(1),
        ob.notes || '-',
        ob.createdBy || '-',
        ob.createdAt ? new Date(ob.createdAt).toLocaleDateString() : '-'
      ];
    });
    
    // Add table
    autoTable(doc, {
      startY: 28,
      head: [['Customer', 'Start Date', 'End Date', 'Status', 'Notes', 'Created By', 'Created At']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: { 
        fillColor: [59, 130, 246], // blue-600
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 40 }, // Customer
        1: { cellWidth: 30 }, // Start Date
        2: { cellWidth: 30 }, // End Date
        3: { cellWidth: 25 }, // Status
        4: { cellWidth: 60 }, // Notes
        5: { cellWidth: 40 }, // Created By
        6: { cellWidth: 30 }  // Created At
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      }
    });
    
    // Add footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Save the PDF
    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`onboarding-calendar-${timestamp}.pdf`);
    
    showToast(`PDF exported successfully (${onboardings.length} records)`, 'success');
  };

  // Calculate next 3 available start dates based on capacity (max 3 concurrent confirmed onboardings)
  const getNextAvailableDates = () => {
    const confirmedOnboardings = onboardings.filter(ob => ob.status === 'confirmed');
    
    if (confirmedOnboardings.length === 0) {
      // No confirmed onboardings, can start anytime
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return [
        new Date(today),
        new Date(today.setDate(today.getDate() + 1)),
        new Date(today.setDate(today.getDate() + 1))
      ];
    }

    // Get all confirmed onboarding periods
    const periods = confirmedOnboardings.map(ob => {
      const start = new Date(ob.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 29);
      return { start, end, customer: ob.customer };
    }).sort((a, b) => a.start - b.start);

    const availableDates = [];
    let searchDate = new Date();
    searchDate.setHours(0, 0, 0, 0);

    // Find dates where we can fit a new 30-day onboarding
    let attempts = 0;
    while (availableDates.length < 3 && attempts < 1000) {
      attempts++;
      const currentSearchDate = new Date(searchDate);
      const potentialEnd = new Date(currentSearchDate);
      potentialEnd.setDate(potentialEnd.getDate() + 29);

      // Count overlapping confirmed onboardings for this period
      const overlapping = periods.filter(period => 
        currentSearchDate <= period.end && potentialEnd >= period.start
      );

      if (overlapping.length < 3) {
        // Found an available slot!
        availableDates.push(new Date(currentSearchDate));
        
        // Jump ahead to find next meaningful date
        // If no overlaps, jump 30 days. If overlaps exist, find next gap
        if (overlapping.length === 0) {
          searchDate.setDate(searchDate.getDate() + 30);
        } else {
          // Move past the current overlapping periods
          const latestEnd = Math.max(...overlapping.map(p => p.end.getTime()));
          searchDate = new Date(latestEnd);
          searchDate.setDate(searchDate.getDate() + 1);
        }
      } else {
        // Capacity full - jump to when one of the overlapping periods ends
        const earliestEnd = Math.min(...overlapping.map(p => p.end.getTime()));
        searchDate = new Date(earliestEnd);
        searchDate.setDate(searchDate.getDate() + 1);
      }
    }

    return availableDates;
  };


  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
  const capacity = getCapacityInfo(currentDate);
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const monthOnboardings = getMonthOnboardings(currentDate);
  const nextAvailableDates = getNextAvailableDates();

  const getStatusColor = (status) => {
    const statusObj = statuses.find(s => s.value === status);
    return statusObj ? statusObj.color : 'bg-gray-500';
  };

  const capacityPercentage = (capacity.confirmed / capacity.total) * 100;
  const capacityColor = capacity.available === 0 ? 'text-red-600' : capacity.available === 1 ? 'text-yellow-600' : 'text-green-600';

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!user) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl shadow-2xl p-12 max-w-md">
          <img 
            src="/Alogo-aptarro.png" 
            alt="Aptarro Logo" 
            className="h-24 mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Aptarro</h1>
          <h2 className="text-2xl font-semibold text-gray-700 mb-6">Customer Onboarding</h2>
          <p className="text-gray-600 mb-8">
            Sign in with your Microsoft account to access the onboarding calendar
          </p>
          <button
            onClick={handleLogin}
            disabled={authLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Users size={20} />
            {authLoading ? 'Signing in...' : 'Sign in with Microsoft'}
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading calendar data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white min-h-screen">
      {/* Header with Logo */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">
            {/* Aptarro Logo */}
            <img 
              src="/Alogo-aptarro.png" 
              alt="Aptarro Logo" 
              className="h-16 w-auto"
              onError={(e) => {
                // Fallback if logo not found - show company name
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="hidden text-3xl font-bold text-blue-600">APTARRO</div>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Aptarro Customer Onboarding</h1>
            <p className="text-gray-600">Track and manage customer onboarding capacity across teams</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={exportToPDF}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            title="Export to PDF"
          >
            <Download size={18} />
            Export PDF
          </button>
          <div className="text-right">
            <p className="text-sm text-gray-600">Signed in as</p>
            <p className="font-semibold text-gray-900">{user.displayName || user.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            title="Sign out"
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>
      </div>

      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="text-blue-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">Monthly Capacity: {monthName}</h2>
          </div>
          <div className={`text-3xl font-bold ${capacityColor}`}>
            {capacity.confirmed} / {capacity.total}
          </div>
        </div>
        
        <div className="mb-3">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Capacity Used</span>
            <span>{capacity.confirmed} confirmed</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all ${capacity.available === 0 ? 'bg-red-500' : capacity.available === 1 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${capacityPercentage}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-white rounded p-3">
            <div className="text-2xl font-bold text-red-600">{capacity.confirmed}</div>
            <div className="text-xs text-gray-600">Confirmed</div>
          </div>
          <div className="bg-white rounded p-3">
            <div className="text-2xl font-bold text-yellow-600">{capacity.pending}</div>
            <div className="text-xs text-gray-600">Pending</div>
          </div>
          <div className="bg-white rounded p-3">
            <div className={`text-2xl font-bold ${capacityColor}`}>{capacity.available}</div>
            <div className="text-xs text-gray-600">Available</div>
          </div>
        </div>

        {capacity.available === 0 && (
          <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded">
            <AlertCircle size={20} />
            <span className="text-sm font-medium">Capacity full for this month!</span>
          </div>
        )}
      </div>

      {/* Scheduled Onboardings and Available Dates Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Scheduled Onboardings - Takes up 2 columns */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar size={20} />
            Scheduled Onboardings This Month ({monthOnboardings.length})
          </h3>
          
          {monthOnboardings.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No onboardings scheduled for this month</p>
          ) : (
            <div className="space-y-2">
              {monthOnboardings.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)).map(ob => (
                <div key={ob.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(ob.status)}`} />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{ob.customer}</div>
                        <div className="text-sm text-gray-600">
                          {(() => {
                            const startDate = new Date(ob.startDate);
                            const endDate = new Date(startDate);
                            endDate.setDate(startDate.getDate() + 29);
                            return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(ob)}
                        className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        disabled={operationLoading}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => confirmDelete(ob.id, ob.customer)}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                        disabled={operationLoading}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 pl-7">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Notes:</label>
                    <div className="flex gap-2">
                      <textarea
                        value={inlineNotes[ob.id] !== undefined ? inlineNotes[ob.id] : (ob.notes || '')}
                        onChange={(e) => setInlineNotes({ ...inlineNotes, [ob.id]: e.target.value })}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="Add notes or comments..."
                        rows="2"
                        disabled={operationLoading}
                      />
                      <button
                        onClick={() => handleInlineNoteSave(ob.id)}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
                        disabled={operationLoading}
                      >
                        {operationLoading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Next Available Dates - Takes up 1 column */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar size={20} />
            Next Available Dates
          </h3>
          
          <div className="space-y-3">
            {nextAvailableDates.map((date, index) => (
              <div key={index} className="p-4 bg-green-50 border-l-4 border-green-500 rounded">
                <div className="text-sm text-gray-600 mb-1">Start Date {index + 1}</div>
                <div className="font-semibold text-gray-900">
                  {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const endDate = new Date(date);
                    endDate.setDate(date.getDate() + 29);
                    return `Through ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                  })()}
                </div>
              </div>
            ))}
          </div>
          
          <button
            onClick={handleScheduleOnboard}
            className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            <Calendar size={18} />
            Schedule Onboard
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handlePrevMonth}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} />
          Previous
        </button>
        <h2 className="text-2xl font-semibold text-gray-800">{monthName}</h2>
        <button
          onClick={handleNextMonth}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Next
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg border border-gray-200 mb-6">
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-50 p-3 text-center font-semibold text-gray-700">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {Array.from({ length: startingDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="bg-gray-50 min-h-20" />
          ))}
          
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
            const dayOnboardings = getOnboardingsForDay(day, month, year);
            
            return (
              <div
                key={day}
                onClick={() => handleDayClick(day)}
                className={`bg-white min-h-20 p-1.5 cursor-pointer hover:bg-blue-50 transition-colors ${isToday ? 'ring-2 ring-blue-500' : ''}`}
              >
                <div className={`text-xs font-semibold mb-0.5 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                  {day}
                </div>
                <div className="space-y-0.5">
                  {/* Show onboardings that overlap with this specific day */}
                  {dayOnboardings.map(ob => (
                    <div
                      key={ob.id}
                      className={`text-[10px] leading-tight py-0.5 px-1 rounded text-white truncate ${getStatusColor(ob.status)}`}
                      title={ob.customer}
                    >
                      {ob.customer}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">{editingId ? 'Edit Onboarding' : 'Add Onboarding'}</h3>
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                <input
                  type="text"
                  value={formData.customer}
                  onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter customer name"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Add any notes or comments..."
                  rows="3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={editingId ? handleSaveEdit : handleAddOnboarding}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  disabled={operationLoading}
                >
                  {operationLoading ? (editingId ? 'Saving...' : 'Adding...') : (editingId ? 'Save Changes' : 'Add Onboarding')}
                </button>
                <button
                  onClick={editingId ? handleCancelEdit : () => setShowModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  disabled={operationLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">Confirm Delete</h3>
              </div>
              
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete the onboarding for <strong>{deleteTarget.customer}</strong>? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => handleDelete(deleteTarget.id)}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium"
                  disabled={operationLoading}
                >
                  {operationLoading ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={cancelDelete}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  disabled={operationLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default OnboardingCalendar;